import React, { useState, useEffect } from 'react';
import { __ } from './translations';
import { Newspaper, Calendar, ArrowRight, Loader, AlertCircle } from 'lucide-react';
import SearchInput from './components/SearchInput';

// Add a helper function to clean the excerpt
const cleanExcerpt = (excerpt: string): string => {
    const div = document.createElement('div');
    div.innerHTML = excerpt;
    const readMoreLink = div.querySelector('.btn.btn-link');
    if (readMoreLink) {
        readMoreLink.remove();
    }
    return div.innerHTML;
};

interface Post {
    id: number;
    title: { rendered: string };
    excerpt: { rendered: string };
    link: string;
    date: string;
    _embedded?: {
        'wp:featuredmedia'?: Array<{
            source_url: string;
            alt_text?: string;
        }>;
    };
}

const BoardMemberPosts: React.FC = () => {
    const [posts, setPosts] = useState<Post[]>([]);
    const [filteredPosts, setFilteredPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const response = await fetch(
                    `${boardMemberData.restURL}wp/v2/posts?meta_key=_board_member_only&meta_value=1&_embed`,
                    {
                        headers: {
                            'X-WP-Nonce': boardMemberData.nonce,
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                setPosts(data);
                setFilteredPosts(data); 
            } catch (error) {
                console.error('Error fetching posts:', error);
                setError(error instanceof Error ? error.message : 'Failed to fetch posts');
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, []);

    if (!boardMemberData.isBoardMember && !boardMemberData.isAdmin) {
        return (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                    <AlertCircle className="h-5 w-5 text-yellow-400" />
                    <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                            {__('You do not have permission to view this content.')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const handleSearch = (searchTerm) => {
        if (!searchTerm.trim()) {
          setFilteredPosts(posts);
          return;
        }
    
        const filtered = posts.filter((post) => {
          const searchString = `${post.title.rendered} ${post.excerpt.rendered}`.toLowerCase();
          return searchString.includes(searchTerm.toLowerCase());
        });
        setFilteredPosts(filtered);
      };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader className="h-8 w-8 text-blue-500 animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4 flex">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                        <Newspaper className="h-5 w-5 text-gray-500" />
                        {__('Board Member Posts')}
                    </h2>
                    <div className="w-1/4 ml-auto">
                    <SearchInput 
        onSearch={handleSearch} 
        placeholder={__('Search posts...')}
                        />
                        </div>
                </div>

                <div className="divide-y divide-gray-200">
                    {filteredPosts.length === 0 ? (
                        <div className="px-6 py-4 text-center text-gray-500">
                            {__('No posts available.')}
                        </div>
                    ) : (
                        filteredPosts.map((post) => (
                            <article key={post.id} className="p-6 hover:bg-gray-50 transition-colors">
                                <div className="flex items-start space-x-6">
                                    {post._embedded?.['wp:featuredmedia']?.[0] && (
                                        <img
                                            src={post._embedded['wp:featuredmedia'][0].source_url}
                                            alt={post._embedded['wp:featuredmedia'][0].alt_text || post.title.rendered}
                                            className="h-32 w-32 object-cover rounded-lg flex-shrink-0"
                                        />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg font-medium text-gray-900 mb-1">
                                            <a
                                                href={post.link}
                                                className="hover:text-blue-600 hover:underline"
                                                dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                                            />
                                        </h3>
                                        <div className="flex items-center text-sm text-gray-500 mb-2">
                                            <Calendar className="h-4 w-4 mr-1.5" />
                                            <time dateTime={post.date}>
                                                {new Date(post.date).toLocaleDateString()}
                                            </time>
                                        </div>
                                        <div
                                            className="text-gray-600 text-sm line-clamp-3 mb-4"
                                            dangerouslySetInnerHTML={{ __html: cleanExcerpt(post.excerpt.rendered) }}
                                        />
                                       
                                    </div>
                                </div>
                            </article>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default BoardMemberPosts;